/**
 * Phase 3.26 — Reservierungs-UI im Listing-Detail.
 * Zeigt:
 *  - "Jetzt reservieren"-Button (B2C/B2B-Käufer, nicht Owner, nicht Auktion)
 *  - Live-Countdown bis expires_at wenn der eingeloggte User bereits reserviert hat
 *  - "Reserviert"-Badge wenn jemand ANDERES reserviert hat
 *  - Hinweis auf Auto-Vertragsversand wenn Seller das aktiviert hat
 */
import { useEffect, useMemo, useState } from "react";
import { Clock, CheckCircle2, AlertCircle, FileSignature, User as UserIcon, Mail, Phone, Zap, FileText, Download } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { previewPdfResponse } from "../lib/pdfPreview";

function formatCountdown(ms) {
    if (ms <= 0) return "0h 0m";
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h >= 24) {
        const d = Math.floor(h / 24);
        return `${d}t ${h % 24}h ${m}m`;
    }
    return `${h}h ${m}m`;
}

export default function ReservationPanel({ listing, onUpdate }) {
    const { user } = useAuth();
    const [mine, setMine] = useState(null);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState("");
    const [showNote, setShowNote] = useState(false);
    const [now, setNow] = useState(Date.now());
    // Feb-2026 (User-Wunsch 27.05.2026) — Pflicht-Kontaktdaten für Reservierung.
    // Werden bei eingeloggten Käufern aus dem Profil vorbefüllt; ohne sie ist
    // keine Reservierung möglich (Backend forciert 422).
    const splitName = (user?.name || "").trim();
    const idxSpace = splitName.indexOf(" ");
    const [form, setForm] = useState({
        first_name: idxSpace > 0 ? splitName.slice(0, idxSpace) : splitName,
        last_name: idxSpace > 0 ? splitName.slice(idxSpace + 1).trim() : "",
        email: user?.email || "",
        phone: user?.phone || "",
    });
    const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const reqEmpty = (k) => !String(form[k] || "").trim();
    const reqCls = (k) => (reqEmpty(k) ? "ring-2 ring-red-300 focus:ring-red-500" : "");

    const isOwner = user && user.id === listing.owner_id;
    const isAuction = listing.sale_type === "auction";
    const reservedByOther = !!listing.reserved_by && (!user || listing.reserved_by !== user.id);
    const reservedByMe = !!listing.reserved_by && user && listing.reserved_by === user.id;
    const sellerAutoDispatch = !!listing.owner?.auto_contract_dispatch_enabled;

    // Sofortkauf — effektiver Wert: pro Inserat überschreibt global (Händlerprofil).
    const sellerDocs = listing.owner || {};
    const instantBuy = listing.instant_buy_enabled != null ? !!listing.instant_buy_enabled : !!sellerDocs.instant_buy_enabled;
    const hasTerms = !!sellerDocs.doc_terms_url;
    const hasPrivacy = !!sellerDocs.doc_privacy_url;
    const hasContract = !!sellerDocs.doc_contract_url;
    const hasAnyDoc = hasTerms || hasPrivacy || hasContract;
    const docUrl = (rel) => (rel?.startsWith("http") ? rel : `${process.env.REACT_APP_BACKEND_URL}${rel}`);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [acceptPrivacy, setAcceptPrivacy] = useState(false);
    const [acceptWiderruf, setAcceptWiderruf] = useState(false);

    // Sofortkauf Phase 2 — Online-Zahlung (Stripe).
    const instantPaymentEnabled = !!sellerDocs.instant_buy_payment_enabled;
    const depositEur = Number(sellerDocs.instant_buy_deposit_eur) || 0;
    const price = Number(listing.price) || 0;
    const [payMode, setPayMode] = useState("full");
    const [polling, setPolling] = useState(false);
    const eur = (n) => Number(n || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
    const cleanUrl = () => window.history.replaceState({}, "", window.location.pathname);

    // Auto-load my reservation when listing is reserved by me.
    useEffect(() => {
        let alive = true;
        if (reservedByMe) {
            api.get(`/reservations/me/listings/${listing.id}`)
                .then((r) => { if (alive) setMine(r.data && r.data.id ? r.data : null); })
                .catch(() => { if (alive) setMine(null); });
        } else {
            setMine(null);
        }
        return () => { alive = false; };
    }, [listing.id, reservedByMe]);

    // Live ticking clock — 1s while a countdown is visible.
    useEffect(() => {
        if (!reservedByMe || !listing.reserved_until) return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [reservedByMe, listing.reserved_until]);

    const expiresAt = useMemo(() => listing.reserved_until ? new Date(listing.reserved_until).getTime() : 0, [listing.reserved_until]);
    const remainingMs = expiresAt - now;

    const reserve = async (instant = false) => {
        if (!user) {
            toast.error("Bitte zuerst einloggen");
            return;
        }
        // Feb-2026 (User-Wunsch 27.05.2026) — Pflichtfeld-Check
        const missing = ["first_name", "last_name", "email", "phone"].filter((k) => !String(form[k] || "").trim());
        if (missing.length) {
            toast.error("Bitte alle Pflichtfelder ausfüllen: Vorname, Nachname, Handynummer, E-Mail.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error("Bitte eine gültige E-Mail-Adresse eingeben.");
            return;
        }
        if (instant) {
            if (hasTerms && !acceptTerms) {
                toast.error("Bitte akzeptieren Sie die AGB, um verbindlich zu kaufen.");
                return;
            }
            if (hasPrivacy && !acceptPrivacy) {
                toast.error("Bitte akzeptieren Sie die Datenschutzerklärung.");
                return;
            }
            if (user?.role !== "b2b" && !acceptWiderruf) {
                toast.error("Bitte nehmen Sie die Widerrufsbelehrung zur Kenntnis.");
                return;
            }
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/listings/${listing.id}/reserve`, {
                note: note.trim() || null,
                buyer_first_name: form.first_name.trim(),
                buyer_last_name: form.last_name.trim(),
                buyer_email: form.email.trim(),
                buyer_phone: form.phone.trim(),
                instant_buy: instant,
                accept_terms: acceptTerms,
                accept_privacy: acceptPrivacy,
                accept_widerruf: acceptWiderruf,
            });
            setMine(data);
            setShowNote(false);
            setNote("");
            toast.success(instant
                ? "Kauf verbindlich bestätigt! Der Kaufvertrag wurde per E-Mail an Sie verschickt."
                : (sellerAutoDispatch
                    ? `Reserviert! Vertrag wurde per E-Mail an Sie verschickt.`
                    : `Reserviert für ${data.reservation_hours}h.`));
            if (onUpdate) await onUpdate();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Aktion fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    // Sofortkauf Phase 2 — Stripe-Checkout starten (Anzahlung oder Vollzahlung).
    const startCheckout = async (resumeReservation = null) => {
        if (!user) {
            toast.error("Bitte zuerst einloggen");
            return;
        }
        const src = resumeReservation || {
            buyer_first_name: form.first_name.trim(),
            buyer_last_name: form.last_name.trim(),
            buyer_email: form.email.trim(),
            buyer_phone: form.phone.trim(),
        };
        if (!resumeReservation) {
            const missing = ["first_name", "last_name", "email", "phone"].filter((k) => !String(form[k] || "").trim());
            if (missing.length) {
                toast.error("Bitte alle Pflichtfelder ausfüllen: Vorname, Nachname, Handynummer, E-Mail.");
                return;
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
                toast.error("Bitte eine gültige E-Mail-Adresse eingeben.");
                return;
            }
            if (hasTerms && !acceptTerms) {
                toast.error("Bitte akzeptieren Sie die AGB, um verbindlich zu kaufen.");
                return;
            }
            if (hasPrivacy && !acceptPrivacy) {
                toast.error("Bitte akzeptieren Sie die Datenschutzerklärung.");
                return;
            }
            if (user?.role !== "b2b" && !acceptWiderruf) {
                toast.error("Bitte nehmen Sie die Widerrufsbelehrung zur Kenntnis.");
                return;
            }
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/listings/${listing.id}/instant-buy/checkout`, {
                origin_url: window.location.origin,
                payment_mode: resumeReservation ? (resumeReservation.payment_mode || "full") : payMode,
                note: note.trim() || null,
                buyer_first_name: src.buyer_first_name,
                buyer_last_name: src.buyer_last_name,
                buyer_email: src.buyer_email,
                buyer_phone: src.buyer_phone,
                accept_terms: resumeReservation ? true : acceptTerms,
                accept_privacy: resumeReservation ? true : acceptPrivacy,
                accept_widerruf: resumeReservation ? true : acceptWiderruf,
            });
            if (data.url) {
                window.location.href = data.url; // redirect to Stripe (keep busy=true)
            } else {
                toast.error("Keine Checkout-URL erhalten");
                setBusy(false);
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout fehlgeschlagen");
            setBusy(false);
        }
    };

    // Poll für Zahlungsstatus nach Rückkehr von Stripe.
    const pollPayment = async (sid, attempt = 0) => {
        setPolling(true);
        try {
            const { data } = await api.get(`/instant-buy/checkout/status/${sid}`);
            if (data.status === "completed" && data.payment_status === "paid") {
                toast.success("Zahlung erfolgreich! Ihr Kauf ist verbindlich bestätigt — der Kaufvertrag wurde per E-Mail verschickt.");
                cleanUrl();
                setPolling(false);
                if (onUpdate) await onUpdate();
                return;
            }
            if (data.status === "expired") {
                toast.error("Die Zahlungssitzung ist abgelaufen. Bitte erneut versuchen.");
                cleanUrl();
                setPolling(false);
                return;
            }
        } catch (_e) { /* keep polling */ }
        if (attempt < 6) {
            setTimeout(() => pollPayment(sid, attempt + 1), 2000);
        } else {
            setPolling(false);
            cleanUrl();
            if (onUpdate) await onUpdate();
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("ib_cancelled")) {
            toast.info("Zahlung abgebrochen. Ihre Reservierung bleibt bestehen, bis sie abläuft.");
            cleanUrl();
            return;
        }
        const sid = params.get("ib_session_id");
        if (sid) pollPayment(sid);
        // run once on mount
    }, []);

    // Don't render at all for owners or auctions.
    if (isOwner || isAuction) return null;

    // CASE: Reserved by someone else
    if (reservedByOther) {
        return (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="reservation-panel-blocked">
                <div className="flex items-center gap-2 text-amber-800">
                    <AlertCircle className="w-5 h-5" />
                    <div className="font-bold text-sm">Aktuell reserviert</div>
                </div>
                <div className="text-xs text-amber-700 mt-1">
                    Dieses Fahrzeug ist bis {new Date(listing.reserved_until).toLocaleString("de-DE")} reserviert.
                </div>
            </div>
        );
    }

    // CASE: Reserved by me — live countdown
    if (reservedByMe) {
        const expired = remainingMs <= 0;
        return (
            <div className={`border rounded-sm p-4 ${expired ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`} data-testid="reservation-panel-mine">
                <div className="flex items-center gap-2 mb-2">
                    {expired ? (
                        <AlertCircle className="w-5 h-5 text-red-700" />
                    ) : (
                        <CheckCircle2 className="w-5 h-5 text-green-700" />
                    )}
                    <div className={`font-bold text-sm ${expired ? "text-red-800" : "text-green-800"}`}>
                        {expired ? "Reservierung abgelaufen" : "Für Sie reserviert"}
                    </div>
                </div>
                {!expired && (
                    <div className="text-center py-2">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-green-700">Verbleibende Zeit</div>
                        <div className="font-mono-num text-2xl font-black text-green-900 flex items-center justify-center gap-1" data-testid="reservation-countdown">
                            <Clock className="w-5 h-5" />
                            {formatCountdown(remainingMs)}
                        </div>
                        <div className="text-[10px] text-green-700 mt-1">
                            bis {new Date(listing.reserved_until).toLocaleString("de-DE")}
                        </div>
                    </div>
                )}
                {mine && mine.contract_dispatched_at && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-green-800 bg-white border border-green-200 rounded-sm p-2" data-testid="reservation-contract-dispatched">
                        <FileSignature className="w-4 h-4 shrink-0" />
                        <span>Kaufvertrag per E-Mail verschickt.</span>
                    </div>
                )}
                {mine?.payment_status === "paid" && (
                    <div className="mt-2 flex items-center gap-2 text-xs font-bold text-green-900 bg-green-100 border border-green-300 rounded-sm p-2" data-testid="reservation-paid-badge">
                        <CheckCircle2 className="w-4 h-4 shrink-0" />
                        <span>Bezahlt{mine.paid_amount_eur ? ` · ${eur(mine.paid_amount_eur)}${mine.payment_mode === "deposit" ? " (Anzahlung)" : ""}` : ""}</span>
                    </div>
                )}
                {mine?.payment_status === "paid" && (
                    <button
                        onClick={async () => {
                            try {
                                const resp = await api.get(`/instant-buy/receipt/${mine.id}.pdf`, { responseType: "blob" });
                                previewPdfResponse(resp, `Zahlungsbeleg_${mine.id.slice(0, 8)}.pdf`);
                            } catch (_e) {
                                toast.error("Beleg konnte nicht geladen werden.");
                            }
                        }}
                        className="mt-2 inline-flex items-center justify-center gap-1.5 w-full text-xs font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1d4ed8] hover:underline"
                        data-testid="reservation-receipt-pdf-btn"
                        title="Zahlungsbeleg als PDF herunterladen"
                    >
                        <Download className="w-3.5 h-3.5" /> Beleg herunterladen
                    </button>
                )}
                {mine?.instant_buy && instantPaymentEnabled && mine?.payment_status !== "paid" && (
                    <Button
                        onClick={() => startCheckout(mine)}
                        disabled={busy}
                        className="mt-2 w-full bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider text-xs"
                        data-testid="reservation-resume-payment-btn"
                    >
                        {busy ? "Wird verarbeitet…" : "Zahlung abschließen"}
                    </Button>
                )}
                {listing.owner?.financing_link_url && (
                    <a
                        href={listing.owner.financing_link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center justify-center w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold rounded-sm py-2 text-xs uppercase tracking-wider"
                        data-testid="reservation-financing-link"
                    >
                        Finanzierung anfragen
                    </a>
                )}
                {!expired && mine?.id && (
                    <button
                        onClick={async () => {
                            if (!window.confirm("Reservierung wirklich stornieren?")) return;
                            try {
                                await api.delete(`/reservations/${mine.id}`);
                                if (onUpdate) await onUpdate();
                                toast.success("Reservierung storniert");
                            } catch (e) {
                                toast.error(e.response?.data?.detail || "Stornierung fehlgeschlagen");
                            }
                        }}
                        className="mt-2 w-full text-xs text-red-700 hover:text-red-900 hover:underline"
                        data-testid="reservation-cancel-btn"
                    >
                        Reservierung stornieren
                    </button>
                )}
            </div>
        );
    }

    // CASE: Free — show CTA
    const requiredMissing = ["first_name", "last_name", "email", "phone"].some((k) => !String(form[k] || "").trim());
    return (
        <div className="bg-[#FFCC00]/10 border-2 border-[#FFCC00] rounded-sm p-4" data-testid="reservation-panel-cta">
            <div className="flex items-center gap-2 text-[#111827] mb-1">
                {instantBuy ? <Zap className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                <div className="font-bold text-sm">{instantBuy ? "Sofortkauf" : "Fahrzeug reservieren"}</div>
            </div>
            <p className="text-xs text-gray-700 mb-3">
                {instantBuy
                    ? "Kaufen Sie dieses Fahrzeug verbindlich ohne Verhandlung. Sie erhalten umgehend den Kaufvertrag per E-Mail."
                    : <>Sichern Sie sich dieses Fahrzeug{listing.owner?.reservation_hours ? ` für ${listing.owner.reservation_hours} Stunden` : ""}.{sellerAutoDispatch && " Sie erhalten umgehend einen vorbereiteten Kaufvertrag per E-Mail."}</>}
            </p>
            {/* Feb-2026 (User-Wunsch 27.05.2026) — Pflicht-Kontaktdaten. Ohne
                diese Felder ist die Reservierung NICHT möglich. */}
            {user && (
                <div className="bg-white border border-gray-200 rounded-sm p-3 mb-3" data-testid="reservation-contact-form">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-2">
                        Kontaktdaten · Pflichtfelder
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                            <Label className="text-[10px] uppercase font-bold text-gray-500 inline-flex items-center gap-1">
                                <UserIcon className="w-3 h-3" /> Vorname
                            </Label>
                            <Input
                                value={form.first_name}
                                onChange={(e) => upd("first_name", e.target.value)}
                                placeholder="Max"
                                className={`mt-1 rounded-sm h-9 text-sm ${reqCls("first_name")}`}
                                data-testid="reservation-first-name"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase font-bold text-gray-500">Nachname</Label>
                            <Input
                                value={form.last_name}
                                onChange={(e) => upd("last_name", e.target.value)}
                                placeholder="Mustermann"
                                className={`mt-1 rounded-sm h-9 text-sm ${reqCls("last_name")}`}
                                data-testid="reservation-last-name"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase font-bold text-gray-500 inline-flex items-center gap-1">
                                <Phone className="w-3 h-3" /> Handynummer
                            </Label>
                            <Input
                                type="tel"
                                value={form.phone}
                                onChange={(e) => upd("phone", e.target.value)}
                                placeholder="+49 170 123 456"
                                className={`mt-1 rounded-sm h-9 text-sm ${reqCls("phone")}`}
                                data-testid="reservation-phone"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase font-bold text-gray-500 inline-flex items-center gap-1">
                                <Mail className="w-3 h-3" /> E-Mail
                            </Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => upd("email", e.target.value)}
                                placeholder="max@mustermann.de"
                                className={`mt-1 rounded-sm h-9 text-sm ${reqCls("email")}`}
                                data-testid="reservation-email"
                            />
                        </div>
                    </div>
                </div>
            )}
            {showNote && (
                <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional: Notiz an den Verkäufer (z.B. Wunsch-Besichtigungstermin)"
                    className="mb-2 rounded-sm text-sm"
                    maxLength={500}
                    rows={2}
                    data-testid="reservation-note-input"
                />
            )}
            {/* Sofortkauf-Consent: AGB + Datenschutz müssen aktiv akzeptiert werden. */}
            {instantBuy && (hasTerms || hasPrivacy || user?.role !== "b2b") && (
                <div className="bg-white border border-gray-200 rounded-sm p-3 mb-3 space-y-2" data-testid="instant-buy-consent">
                    {hasTerms && (
                        <label className="flex items-start gap-2 cursor-pointer text-xs text-gray-700">
                            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FFCC00]" data-testid="instant-buy-accept-terms" />
                            <span>Ich akzeptiere die <a href={docUrl(sellerDocs.doc_terms_url)} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">AGB</a>.</span>
                        </label>
                    )}
                    {hasPrivacy && (
                        <label className="flex items-start gap-2 cursor-pointer text-xs text-gray-700">
                            <input type="checkbox" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FFCC00]" data-testid="instant-buy-accept-privacy" />
                            <span>Ich habe die <a href={docUrl(sellerDocs.doc_privacy_url)} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Datenschutzerklärung</a> gelesen.</span>
                        </label>
                    )}
                    {user?.role !== "b2b" && (
                        <label className="flex items-start gap-2 cursor-pointer text-xs text-gray-700">
                            <input type="checkbox" checked={acceptWiderruf} onChange={(e) => setAcceptWiderruf(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FFCC00]" data-testid="instant-buy-accept-widerruf" />
                            <span>Ich habe die <a href="/widerruf" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Widerrufsbelehrung</a> zur Kenntnis genommen.</span>
                        </label>
                    )}
                </div>
            )}
            {/* Sofortkauf Phase 2 — Zahlungsmodus (Anzahlung / Vollzahlung). */}
            {instantBuy && instantPaymentEnabled && (
                <div className="bg-white border border-gray-200 rounded-sm p-3 mb-3" data-testid="instant-buy-payment-modes">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-2">Zahlung</div>
                    {depositEur > 0 ? (
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="radio" name="ib-pay-mode" value="full" checked={payMode === "full"} onChange={() => setPayMode("full")} className="w-4 h-4 accent-[#FFCC00]" data-testid="instant-buy-mode-full" />
                                <span className="flex-1">Vollzahlung</span>
                                <span className="font-bold">{eur(price)}</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="radio" name="ib-pay-mode" value="deposit" checked={payMode === "deposit"} onChange={() => setPayMode("deposit")} className="w-4 h-4 accent-[#FFCC00]" data-testid="instant-buy-mode-deposit" />
                                <span className="flex-1">Anzahlung <span className="text-gray-500 text-xs">(Rest beim Verkäufer)</span></span>
                                <span className="font-bold">{eur(depositEur)}</span>
                            </label>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between text-sm" data-testid="instant-buy-mode-full-only">
                            <span>Online-Zahlung Kaufpreis</span>
                            <span className="font-bold">{eur(price)}</span>
                        </div>
                    )}
                </div>
            )}
            <div className="flex gap-2">
                {!showNote && (
                    <Button
                        type="button"
                        variant="outline"
                        className="rounded-sm text-xs border-[#111827] flex-shrink-0"
                        onClick={() => setShowNote(true)}
                        data-testid="reservation-add-note-btn"
                    >+ Notiz</Button>
                )}
                <Button
                    onClick={() => (instantBuy && instantPaymentEnabled ? startCheckout() : reserve(instantBuy))}
                    disabled={busy || polling || !user || requiredMissing}
                    className="flex-1 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider disabled:opacity-60"
                    data-testid={instantBuy ? "instant-buy-btn" : "reservation-reserve-btn"}
                    title={requiredMissing ? "Bitte alle Pflichtfelder ausfüllen" : ""}
                >
                    {busy || polling ? "Wird verarbeitet…" : (!user ? "Bitte einloggen" : (instantBuy ? (instantPaymentEnabled ? "Jetzt kaufen & bezahlen" : "Jetzt verbindlich kaufen") : "Jetzt reservieren"))}
                </Button>
            </div>
            {/* Rechtliche Dokumente des Verkäufers (3c) — immer verfügbar, wenn hinterlegt. */}
            {hasAnyDoc && (
                <div className="mt-3 pt-3 border-t border-[#FFCC00]/40 flex flex-wrap gap-x-3 gap-y-1 text-[11px]" data-testid="seller-legal-doc-links">
                    {hasPrivacy && <a href={docUrl(sellerDocs.doc_privacy_url)} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#111827] underline inline-flex items-center gap-1" data-testid="doc-link-privacy"><FileText className="w-3 h-3" />Datenschutz</a>}
                    {hasTerms && <a href={docUrl(sellerDocs.doc_terms_url)} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#111827] underline inline-flex items-center gap-1" data-testid="doc-link-terms"><FileText className="w-3 h-3" />AGB</a>}
                    {hasContract && <a href={docUrl(sellerDocs.doc_contract_url)} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#111827] underline inline-flex items-center gap-1" data-testid="doc-link-contract"><FileText className="w-3 h-3" />Kaufvertrag</a>}
                </div>
            )}
        </div>
    );
}
